import React from 'react'
import { useNavigate } from 'react-router-dom'
import AppDateInput from '../../../components/AppDateInput'
import AppCombobox from '../../../components/AppCombobox'
import AppFormFooterBar from '../../../components/AppFormFooterBar'
import EventTipsCard from './components/EventTipsCard'
import useEventsEntry from './hooks/useEventsEntry'

export default function EventsEntryScreen() {
  const navigate = useNavigate()
  const { state, actions } = useEventsEntry()

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-28">
      {/* ── Header ── */}
      <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
            Cloud &amp; Campus Engagement
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {state.isEditMode ? 'Edit Event' : 'Add New Event'}
          </h1>
          <p className="mt-0.5 text-xs text-on-surface-variant">
            {state.isEditMode
              ? 'Modify scheduled event details, target batches, and published particulars.'
              : 'Schedule institution seminars, academic workshops, exams, and annual celebrations.'}
          </p>
        </div>
      </section>

      {/* ── Main 2-Column Grid: 3 Form Cards (8 cols) + Sticky TIPS Panel (4 cols) ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-start">
        {/* ── Left Column: Form Cards ── */}
        <div className="lg:col-span-8 flex flex-col gap-6">
          {/* ── Card 1: Schedule & Timing ── */}
          <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-xs">
            <div className="border-b border-outline-variant/20 bg-surface-container-low/60 px-6 py-4 rounded-t-3xl flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-500/10 text-blue-600">
                <span className="material-symbols-outlined text-lg">calendar_today</span>
              </div>
              <div>
                <h2 className="font-display text-base font-bold text-on-surface">
                  Event Schedule &amp; Date
                </h2>
                <p className="text-xs text-on-surface-variant">
                  Specify the date on which the event is scheduled to take place.
                </p>
              </div>
            </div>

            <div className="p-6 md:p-8">
              <div className="grid grid-cols-12 gap-4 items-center">
                <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Event Date <span className="text-rose-500 font-bold">*</span>
                </span>
                <div className="col-span-12 sm:col-span-6">
                  <AppDateInput
                    value={state.tDate}
                    onChange={actions.setTDate}
                    placeholder="dd-mm-yyyy"
                    inputClassName="h-10 text-xs font-bold"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* ── Card 2: Organization & Target Scope ── */}
          <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-xs">
            <div className="border-b border-outline-variant/20 bg-surface-container-low/60 px-6 py-4 rounded-t-3xl flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600">
                <span className="material-symbols-outlined text-lg">corporate_fare</span>
              </div>
              <div>
                <h2 className="font-display text-base font-bold text-on-surface">
                  Organization &amp; Target Scope
                </h2>
                <p className="text-xs text-on-surface-variant">
                  Configure audience reach by individual institution, branch, course, or batch.
                </p>
              </div>
            </div>

            <div className="p-6 md:p-8 flex flex-col gap-5 text-xs">
              {/* Institute Row */}
              <div className="grid grid-cols-12 gap-4 items-center">
                <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Institute
                </span>
                <div className="col-span-12 sm:col-span-6">
                  <AppCombobox
                    options={state.options.institutes}
                    value={state.instituteId}
                    onChange={actions.setInstituteId}
                    disabled={state.instituteType === 2}
                    placeholder="Select Institute..."
                    triggerClassName="h-10 w-full bg-surface-container-low text-xs font-semibold disabled:opacity-50"
                  />
                </div>
                <div className="col-span-12 sm:col-span-3">
                  <div className="inline-flex items-center rounded-xl bg-surface-container-high p-1 select-none">
                    <button
                      type="button"
                      onClick={() => actions.setInstituteType(1)}
                      className={`rounded-lg px-3 py-1 text-xs font-bold transition-all cursor-pointer ${
                        state.instituteType === 1
                          ? 'bg-surface-container-lowest text-primary shadow-xs'
                          : 'text-on-surface-variant hover:text-on-surface'
                      }`}
                    >
                      Single
                    </button>
                    <button
                      type="button"
                      onClick={() => actions.setInstituteType(2)}
                      className={`rounded-lg px-3 py-1 text-xs font-bold transition-all cursor-pointer ${
                        state.instituteType === 2
                          ? 'bg-surface-container-lowest text-primary shadow-xs'
                          : 'text-on-surface-variant hover:text-on-surface'
                      }`}
                    >
                      All
                    </button>
                  </div>
                </div>
              </div>

              {/* Branch Row */}
              <div className="grid grid-cols-12 gap-4 items-center">
                <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Branch
                </span>
                <div className="col-span-12 sm:col-span-6">
                  <AppCombobox
                    options={state.options.branches}
                    value={state.branchId}
                    onChange={actions.setBranchId}
                    disabled={state.branchType === 2}
                    placeholder="Select Branch..."
                    triggerClassName="h-10 w-full bg-surface-container-low text-xs font-semibold disabled:opacity-50"
                  />
                </div>
                <div className="col-span-12 sm:col-span-3">
                  <div className="inline-flex items-center rounded-xl bg-surface-container-high p-1 select-none">
                    <button
                      type="button"
                      onClick={() => actions.setBranchType(1)}
                      className={`rounded-lg px-3 py-1 text-xs font-bold transition-all cursor-pointer ${
                        state.branchType === 1
                          ? 'bg-surface-container-lowest text-primary shadow-xs'
                          : 'text-on-surface-variant hover:text-on-surface'
                      }`}
                    >
                      Single
                    </button>
                    <button
                      type="button"
                      onClick={() => actions.setBranchType(2)}
                      className={`rounded-lg px-3 py-1 text-xs font-bold transition-all cursor-pointer ${
                        state.branchType === 2
                          ? 'bg-surface-container-lowest text-primary shadow-xs'
                          : 'text-on-surface-variant hover:text-on-surface'
                      }`}
                    >
                      All
                    </button>
                  </div>
                </div>
              </div>

              {/* Course Row */}
              <div className="grid grid-cols-12 gap-4 items-center">
                <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Course
                </span>
                <div className="col-span-12 sm:col-span-6">
                  <AppCombobox
                    options={state.options.courses}
                    value={state.courseId}
                    onChange={actions.setCourseId}
                    disabled={state.courseType === 2}
                    placeholder="Select Course..."
                    triggerClassName="h-10 w-full bg-surface-container-low text-xs font-semibold disabled:opacity-50"
                  />
                </div>
                <div className="col-span-12 sm:col-span-3">
                  <div className="inline-flex items-center rounded-xl bg-surface-container-high p-1 select-none">
                    <button
                      type="button"
                      onClick={() => actions.setCourseType(1)}
                      className={`rounded-lg px-3 py-1 text-xs font-bold transition-all cursor-pointer ${
                        state.courseType === 1
                          ? 'bg-surface-container-lowest text-primary shadow-xs'
                          : 'text-on-surface-variant hover:text-on-surface'
                      }`}
                    >
                      Single
                    </button>
                    <button
                      type="button"
                      onClick={() => actions.setCourseType(2)}
                      className={`rounded-lg px-3 py-1 text-xs font-bold transition-all cursor-pointer ${
                        state.courseType === 2
                          ? 'bg-surface-container-lowest text-primary shadow-xs'
                          : 'text-on-surface-variant hover:text-on-surface'
                      }`}
                    >
                      All
                    </button>
                  </div>
                </div>
              </div>

              {/* Batch Row */}
              <div className="grid grid-cols-12 gap-4 items-center">
                <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Batch
                </span>
                <div className="col-span-12 sm:col-span-6">
                  <AppCombobox
                    options={state.options.batches}
                    value={state.batchId}
                    onChange={actions.setBatchId}
                    disabled={state.batchType === 2}
                    placeholder="Select Batch..."
                    triggerClassName="h-10 w-full bg-surface-container-low text-xs font-semibold disabled:opacity-50"
                  />
                </div>
                <div className="col-span-12 sm:col-span-3">
                  <div className="inline-flex items-center rounded-xl bg-surface-container-high p-1 select-none">
                    <button
                      type="button"
                      onClick={() => actions.setBatchType(1)}
                      className={`rounded-lg px-3 py-1 text-xs font-bold transition-all cursor-pointer ${
                        state.batchType === 1
                          ? 'bg-surface-container-lowest text-primary shadow-xs'
                          : 'text-on-surface-variant hover:text-on-surface'
                      }`}
                    >
                      Single
                    </button>
                    <button
                      type="button"
                      onClick={() => actions.setBatchType(2)}
                      className={`rounded-lg px-3 py-1 text-xs font-bold transition-all cursor-pointer ${
                        state.batchType === 2
                          ? 'bg-surface-container-lowest text-primary shadow-xs'
                          : 'text-on-surface-variant hover:text-on-surface'
                      }`}
                    >
                      All
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Card 3: Event Particulars & Topic ── */}
          <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-xs">
            <div className="border-b border-outline-variant/20 bg-surface-container-low/60 px-6 py-4 rounded-t-3xl flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <span className="material-symbols-outlined text-lg">event_note</span>
              </div>
              <div>
                <h2 className="font-display text-base font-bold text-on-surface">
                  Event Particulars &amp; Topic
                </h2>
                <p className="text-xs text-on-surface-variant">
                  Enter event title and detailed agenda, venue, or speaker instructions.
                </p>
              </div>
            </div>

            <div className="p-6 md:p-8 flex flex-col gap-5 text-xs">
              {/* Event Title */}
              <div className="grid grid-cols-12 gap-4 items-center">
                <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant">
                  Event Topic <span className="text-rose-500 font-bold">*</span>
                </span>
                <div className="col-span-12 sm:col-span-9">
                  <input
                    type="text"
                    placeholder="e.g. Annual Academic Orientation / Science Olympiad Bootcamp"
                    value={state.title}
                    onChange={(e) => actions.setTitle(e.target.value)}
                    className="h-10 w-full rounded-2xl border border-outline-variant/30 bg-surface-container-low px-4 text-xs font-bold text-on-surface outline-none focus:border-primary"
                  />
                </div>
              </div>

              {/* Particulars */}
              <div className="grid grid-cols-12 gap-4 items-start">
                <span className="col-span-12 sm:col-span-3 text-xs font-black uppercase tracking-wider text-on-surface-variant pt-2">
                  Particulars
                </span>
                <div className="col-span-12 sm:col-span-9">
                  <textarea
                    rows={5}
                    placeholder="Enter comprehensive event description, instructions, guidelines, or venue details..."
                    value={state.particulars}
                    onChange={(e) => actions.setParticulars(e.target.value)}
                    className="w-full rounded-2xl border border-outline-variant/30 bg-surface-container-low p-3.5 text-xs text-on-surface outline-none focus:border-primary resize-y leading-relaxed"
                  />
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* ── Right Column: Sticky TIPS & Guidelines Panel ── */}
        <div className="lg:col-span-4 sticky top-6">
          <EventTipsCard />
        </div>
      </div>

      {/* ── Global Sticky Form Footer Bar ── */}
      <AppFormFooterBar
        onSave={actions.handleSave}
        onCancel={() => navigate('/cloud/events/list')}
        isSaving={state.isSaving}
        saveText="Save"
        cancelText="Cancel"
      />
    </div>
  )
}
